import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import UserLayout from '../../components/user/UserLayout';
import toast from 'react-hot-toast';
import { Trophy, Lock, CheckCircle, Gift, Clock, Star } from 'lucide-react';

const STATUS_LABELS = {
  pending: { text: 'Reward Pending', class: 'bg-amber-500/20 text-amber-300', icon: <Clock className="w-3 h-3" /> },
  processing: { text: 'Processing', class: 'bg-blue-500/20 text-blue-300', icon: <Clock className="w-3 h-3" /> },
  delivered: { text: 'Delivered ✓', class: 'bg-primary-500/20 text-primary-300', icon: <CheckCircle className="w-3 h-3" /> },
  cash_credited: { text: 'Cash Credited ✓', class: 'bg-primary-500/20 text-primary-300', icon: <CheckCircle className="w-3 h-3" /> },
};

export default function AchievementsPage() {
  const { user } = useAuth();
  const [ranks, setRanks] = useState([]);
  const [progress, setProgress] = useState({ directSince: 0, teamSince: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get('/api/achievements/ranks-rewards')
      .then(r => {
        setRanks(r.data.ranks || []);
        setProgress(r.data.progress || { directSince: 0, teamSince: 0 });
      })
      .finally(() => setLoading(false));
  }, []);

  const requestClaim = async (achievementId) => {
    try {
      setLoading(true);
      await axios.post(`/api/achievements/${achievementId}/request-claim`);
      toast.success('Claim request sent to admin');
      const r = await axios.get('/api/achievements/ranks-rewards');
      setRanks(r.data.ranks || []);
      setProgress(r.data.progress || { directSince: 0, teamSince: 0 });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send claim request');
    } finally {
      setLoading(false);
    }
  };

  const currentLevel = user?.level || 0;
  const achievedRanks = ranks.filter(r => r.achieved);
  const nextRank = ranks.find(r => !r.achieved);

  return (
    <UserLayout>
      <div className="animate-fade-in">
        <h1 className="text-2xl font-bold text-white mb-2">Ranks & Rewards</h1>
        <p className="text-gray-400 mb-6">Complete rank achievements and win exciting rewards!</p>

        {/* Current Level Banner */}
        <div className="bg-gradient-to-br from-primary-600/20 to-purple-600/20 border border-primary-500/30 rounded-2xl p-5 mb-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="text-gray-400 text-sm">Your Current Level</p>
              <div className="flex items-center gap-3 mt-1">
                <div className="w-14 h-14 bg-primary-500/30 rounded-2xl flex items-center justify-center">
                  <Trophy className="w-7 h-7 text-primary-400" />
                </div>
                <div>
                  <p className="text-white font-bold text-2xl">
                    {currentLevel === 0 ? 'Starter' : ranks.find(r => r.level === currentLevel)?.name || `Level ${currentLevel}`}
                  </p>
                  <p className="text-gray-400 text-sm">Level {currentLevel}</p>
                </div>
              </div>
            </div>
            {nextRank && (
              <div className="text-right">
                <p className="text-gray-400 text-xs mb-1">Next Rank</p>
                <p className="text-white font-semibold">{nextRank.name}</p>
                {nextRank.level >= 8 ? (
                  <p className="text-gray-500 text-xs">
                    {nextRank.l7DirectCount || 3} direct Level 7 referrals per node • depth {nextRank.level - 7}
                  </p>
                ) : (
                  <p className="text-gray-500 text-xs">{nextRank.directRequired} direct / {nextRank.teamRequired.toLocaleString()} team</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Achieved Rewards */}
        {achievedRanks.filter(r => r.rewardEnabled && r.achievementData).length > 0 && (
          <div className="mb-6">
            <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
              <Gift className="w-5 h-5 text-amber-400" /> Your Rewards
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              {achievedRanks.filter(r => r.rewardEnabled && r.achievementData).map(r => {
                const statusInfo = STATUS_LABELS[r.achievementData?.rewardStatus] || STATUS_LABELS.pending;
                return (
                  <div key={r.level}
                    className="bg-dark-700 border border-primary-500/30 rounded-2xl p-5 flex gap-4">
                    {r.rewardImage ? (
                      <img src={r.rewardImage} alt={r.rewardTitle}
                        className="w-16 h-16 rounded-xl object-cover flex-shrink-0 border border-white/10" />
                    ) : (
                      <div className="w-16 h-16 rounded-xl flex-shrink-0 flex items-center justify-center text-3xl"
                        style={{ background: `${r.rewardBadgeColor}20` }}>
                        {r.rewardType === 'cash' ? '💰' : r.rewardType === 'phone' ? '📱' :
                          r.rewardType === 'laptop' ? '💻' : r.rewardType === 'travel' ? '✈️' :
                          r.rewardType === 'car' ? '🚗' : '🎁'}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-white font-semibold">{r.rewardTitle || r.rewardType}</p>
                          <p className="text-gray-400 text-xs mt-0.5">{r.name} — Level {r.level}</p>
                        </div>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusInfo.class}`}>
                          {statusInfo.icon} {statusInfo.text}
                        </span>
                      </div>
                      {r.rewardValue > 0 && (
                        <p className="text-primary-400 font-bold mt-1">${r.rewardValue.toLocaleString()}</p>
                      )}
                      {r.rewardDescription && (
                        <p className="text-gray-500 text-xs mt-1">{r.rewardDescription}</p>
                      )}
                      {r.achievementData?.adminNote && (
                        <p className="text-gray-500 text-xs mt-1 italic">{r.achievementData.adminNote}</p>
                      )}
                      {r.achievementData?.rewardStatus === 'pending' && (
                        <div className="mt-3">
                          <button onClick={() => requestClaim(r.achievementData._id)}
                            className="bg-amber-500 text-dark-900 px-3 py-1.5 rounded-lg text-sm font-medium">
                            Reward Claim Request
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* All Ranks */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="space-y-4">
            {ranks.map(r => {
              const isAchieved = r.achieved;
              const isCurrent = r.level === currentLevel;
              const isNext = !isAchieved && r.level === (currentLevel + 1);

              return (
                <div key={r.level}
                  className={`relative bg-dark-700 border rounded-2xl p-5 transition-all ${
                    isAchieved ? 'border-primary-500/40 bg-primary-500/5' :
                    isNext ? 'border-amber-500/40' :
                    'border-white/5 opacity-70'
                  }`}>
                  {/* Achieved Badge */}
                  {isAchieved && (
                    <div className="absolute top-4 right-4">
                      <CheckCircle className="w-6 h-6 text-primary-500" />
                    </div>
                  )}
                  {!isAchieved && (
                    <div className="absolute top-4 right-4">
                      <Lock className="w-5 h-5 text-gray-600" />
                    </div>
                  )}

                  <div className="flex gap-4">
                    {/* Level Badge */}
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 ${
                      isAchieved ? 'bg-primary-500/20' : 'bg-dark-600'
                    }`}>
                      <div className="text-center">
                        <Trophy className={`w-6 h-6 mx-auto ${isAchieved ? 'text-primary-400' : 'text-gray-600'}`} />
                        <p className={`text-xs font-bold ${isAchieved ? 'text-primary-300' : 'text-gray-600'}`}>L{r.level}</p>
                      </div>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className={`font-bold text-lg ${isAchieved ? 'text-white' : 'text-gray-500'}`}>{r.name}</h3>
                        {isNext && <span className="bg-amber-500/20 text-amber-300 text-xs px-2 py-0.5 rounded-full">Next Goal</span>}
                        {isCurrent && <span className="bg-primary-500/20 text-primary-300 text-xs px-2 py-0.5 rounded-full">Current</span>}
                      </div>

                      {/* Requirements */}
                      <div className="flex flex-wrap gap-4 mb-3 text-xs">
                        {r.level >= 8 ? (
                          <span className={isAchieved ? 'text-primary-300' : 'text-gray-500'}>
                            🌲 {r.l7DirectCount || 3} direct Level 7 referrals per node • depth {r.level - 7}
                          </span>
                        ) : (
                          <>
                            <span className={isAchieved ? 'text-primary-300' : 'text-gray-500'}>
                              👥 {r.directRequired} Direct
                            </span>
                            <span className={isAchieved ? 'text-primary-300' : 'text-gray-500'}>
                              🌐 {r.teamRequired.toLocaleString()} Team
                            </span>
                            <span className={isAchieved ? 'text-primary-300' : 'text-gray-500'}>
                              📊 Max {r.maxLegPercent}% per leg
                            </span>
                          </>
                        )}
                      </div>

                      {/* Commissions */}
                      <div className="flex gap-3 mb-3">
                        <div className="bg-dark-600 rounded-lg px-3 py-1.5 text-xs">
                          <span className="text-gray-500">Setup Fee: </span>
                          <span className={`font-semibold ${isAchieved ? 'text-primary-300' : 'text-gray-500'}`}>
                            {r.setupFeeCommission}%
                          </span>
                        </div>
                        <div className="bg-dark-600 rounded-lg px-3 py-1.5 text-xs">
                          <span className="text-gray-500">Profit Share: </span>
                          <span className={`font-semibold ${isAchieved ? 'text-green-300' : 'text-gray-500'}`}>
                            {r.profitCommission}%
                          </span>
                        </div>
                      </div>

                      {/* Reward */}
                      {r.rewardEnabled && (
                        <div className={`flex items-center gap-3 p-3 rounded-xl border ${
                          isAchieved
                            ? 'border-amber-500/30 bg-amber-500/10'
                            : 'border-gray-700 bg-dark-600'
                        }`}>
                          {r.rewardImage ? (
                            <img src={r.rewardImage} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                          ) : (
                            <div className="w-10 h-10 rounded-lg flex items-center justify-center text-xl flex-shrink-0"
                              style={{ background: `${r.rewardBadgeColor}20` }}>
                              {r.rewardType === 'cash' ? '💰' : r.rewardType === 'phone' ? '📱' :
                                r.rewardType === 'laptop' ? '💻' : r.rewardType === 'travel' ? '✈️' :
                                r.rewardType === 'car' ? '🚗' : '🎁'}
                            </div>
                          )}
                          <div>
                            <div className="flex items-center gap-2">
                              <Gift className="w-3 h-3 text-amber-400" />
                              <span className="text-amber-300 text-xs font-medium">Rank Reward</span>
                            </div>
                            <p className={`text-sm font-semibold ${isAchieved ? 'text-white' : 'text-gray-400'}`}>
                              {r.rewardTitle || r.rewardType}
                            </p>
                            {r.rewardValue > 0 && (
                              <p className="text-xs" style={{ color: r.rewardBadgeColor }}>
                                ${r.rewardValue.toLocaleString()}
                              </p>
                            )}
                            {r.rewardDescription && (
                              <p className="text-gray-500 text-xs mt-0.5">{r.rewardDescription}</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Progress bar for next rank */}
                  {isNext && (
                    <div className="mt-4 pt-3 border-t border-white/10">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <div className="flex justify-between text-xs mb-1">
                            <span className="text-gray-400">Direct Referrals</span>
                            <span className="text-white">{progress?.directSince ?? (user?.directReferrals || 0)} / {r.directRequired}</span>
                          </div>
                          <div className="h-1.5 bg-dark-600 rounded-full overflow-hidden">
                            <div className="h-full bg-primary-500 rounded-full"
                              style={{ width: `${Math.min(100, ((progress?.directSince ?? (user?.directReferrals || 0)) / r.directRequired) * 100)}%` }}></div>
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between text-xs mb-1">
                            <span className="text-gray-400">Team Size</span>
                            <span className="text-white">{progress?.teamSince ?? (user?.teamSize || 0)} / {r.teamRequired.toLocaleString()}</span>
                          </div>
                          <div className="h-1.5 bg-dark-600 rounded-full overflow-hidden">
                            <div className="h-full bg-amber-500 rounded-full"
                              style={{ width: `${Math.min(100, ((progress?.teamSince ?? (user?.teamSize || 0)) / r.teamRequired) * 100)}%` }}></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </UserLayout>
  );
}
